import {observer} from 'mobx-react';
import React from 'react';
import {X8Block} from '../../../domain';
import {PageHeading} from '../../../lib/components';
import {ReactNodeGenerator} from '../../../lib/helpers';
import {AddBlockControls} from './block-parts/AddBlockControls';
import {DeleteBlockControls} from './block-parts/DeleteBlockControls';
import {ReorderBlockHeaderActions} from './block-parts/ReorderBlockHeaderActions';
import {ReorderBlocksControls} from './block-parts/ReorderBlocksControls';
import {X8BlockComponent} from './content-parts/X8BlockComponent';
import {X7PushableBlockBreadcrumbs} from './parts/X7PushableBlockBreadcrumbs';
import {X7PushableBlockCardHeader} from './parts/X7PushableBlockCardHeader';
import {X7PushableBlockLoaderState} from './parts/X7PushableBlockLoaderState';
import {X7PushableBlockStore} from './X7PushableBlockStore';

function ViewInactiveBlock(block: X8Block, store: X7PushableBlockStore): React.ReactNode {
  return (<X8BlockComponent key={'block-' + block.Id}
                            mode="view-inactive"
                            store={store}
                            block={block}/>);
}

function* ViewModeItems(store: X7PushableBlockStore): ReactNodeGenerator {
  const blocks = store.loader.blocks!;

  if (blocks.length === 0) {
    yield (
      <div className="row justify-content-center" key="no-blocks">
        <div className="col-auto">
          <div className="alert alert-secondary">
            empty pushable block
          </div>
        </div>
      </div>
    );
    return;
  }

  for (let block of blocks)
    yield (<X8BlockComponent key={'block-' + block.Id}
                             mode="view-with-actions"
                             store={store}
                             block={block}/>);
}

function* AddBlockItems(store: X7PushableBlockStore): ReactNodeGenerator {
  const blocks = store.loader.blocks!;

  yield (<AddBlockControls key="add-block-state"
                           store={store}/>);

  for (let block of blocks)
    yield (ViewInactiveBlock(block, store));
}

function* ReorderBlocksItems(store: X7PushableBlockStore): ReactNodeGenerator {
  yield (<ReorderBlocksControls key="reorder-blocks-state"
                                store={store}/>);

  const reorderBlocks = store.reorderBlocks!;
  const blockIds = reorderBlocks.blockIds;
  const blocksById = store.loader.blocksById;
  for (let index = 0; index < blockIds.length; index++) {
    const blockId = blockIds[index];
    const first = index === 0;
    const last = index === blockIds.length - 1;
    const block = blocksById[blockId];

    const reorderBlockHeaderActions = () => (
      <ReorderBlockHeaderActions store={store}
                                 index={index}
                                 first={first}
                                 last={last}/>
    );

    yield (<X8BlockComponent key={'block-' + block.Id}
                             mode="view-inactive"
                             store={store}
                             block={block}
                             headerContent={reorderBlockHeaderActions}
    />);
  }
}

function* DeleteBlockItems(store: X7PushableBlockStore): ReactNodeGenerator {
  const blocks = store.loader.blocks!;
  const deleteBlockId = store.deleteBlock!.blockId;

  for (let block of blocks) {
    if (block.Id !== deleteBlockId) {
      yield ViewInactiveBlock(block, store);
      continue;
    }

    yield (<DeleteBlockControls key="delete-block-controls"
                                store={store}/>);

    const headerActions = () => (
      <button type="button" className="btn btn-danger"
              onClick={() => store.beginDeleteBlock(deleteBlockId)}>
        Delete
      </button>
    );

    yield (<X8BlockComponent key={'block-' + block.Id}
                             mode="view-inactive"
                             store={store}
                             block={block}
                             headerContent={headerActions}/>);
  }
}

function* AddContentItems(store: X7PushableBlockStore): ReactNodeGenerator {
  const blocks = store.loader.blocks!;
  const addContentBlockId = store.addContent!.blockId;

  for (let block of blocks) {
    if (block.Id !== addContentBlockId) {
      yield (ViewInactiveBlock(block, store));
      continue;
    }

    yield (<X8BlockComponent key={'block-' + block.Id}
                             mode="add-content"
                             store={store}
                             block={block}/>);
  }
}

function* EditContentItems(store: X7PushableBlockStore): ReactNodeGenerator {
  const blocks = store.loader.blocks!;
  const editContentBlockId = store.editContent!.content.BlockId;

  for (let block of blocks) {
    if (block.Id !== editContentBlockId) {
      yield (ViewInactiveBlock(block, store));
      continue;
    }

    yield (<X8BlockComponent key={'block-' + block.Id}
                             mode="edit-content"
                             store={store}
                             block={block}/>);
  }
}

function* ReorderContentsItems(store: X7PushableBlockStore): ReactNodeGenerator {
  const blocks = store.loader.blocks!;
  const reorderContentsBlockId = store.reorderContents!.blockId;

  for (let block of blocks) {
    if (block.Id !== reorderContentsBlockId) {
      yield (ViewInactiveBlock(block, store));
      continue;
    }

    yield (<X8BlockComponent key={'block-' + block.Id}
                             mode="reorder-contents"
                             store={store}
                             block={block}/>);
  }
}

function* DeleteContentItems(store: X7PushableBlockStore): ReactNodeGenerator {
  const blocks = store.loader.blocks!;
  const deleteContentBlockId = store.deleteContent!.blockId;

  for (let block of blocks) {
    if (block.Id !== deleteContentBlockId) {
      yield (ViewInactiveBlock(block, store));
      continue;
    }

    yield (<X8BlockComponent key={'block-' + block.Id}
                             mode="delete-content"
                             store={store}
                             block={block}/>);
  }
}

function* cardBodyRenderer(coordinator: X7PushableBlockStore): ReactNodeGenerator {
  const mode = coordinator.mode;

  switch (mode) {
    case 'view':
      yield* ViewModeItems(coordinator);
      return;
    case 'add-block':
      yield* AddBlockItems(coordinator);
      return;
    case 'reorder-blocks':
      yield* ReorderBlocksItems(coordinator);
      return;
    case 'delete-block':
      yield* DeleteBlockItems(coordinator);
      return;
    case 'add-content':
      yield* AddContentItems(coordinator);
      return;
    case 'edit-content':
      yield* EditContentItems(coordinator);
      return;
    case 'reorder-contents':
      yield* ReorderContentsItems(coordinator);
      return;
    case 'delete-content':
      yield* DeleteContentItems(coordinator);
      return;
  }
}

function* componentRenderer(coordinator: X7PushableBlockStore): ReactNodeGenerator {
  const loader = coordinator.loader;

  const pushableBlock = loader.pushableBlock;

  yield (<X7PushableBlockLoaderState key="pushable-block-loader"
                                     loader={loader}/>);

  if (pushableBlock === null)
    return;

  yield (<X7PushableBlockBreadcrumbs key="breadcrumbs"
                                     pushableBlock={pushableBlock}/>);

  yield (<PageHeading key="heading"> Pushable block {pushableBlock.Name} </PageHeading>);

  yield (
    <div className="row" key="main">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <X7PushableBlockCardHeader store={coordinator}/>
          </div>

          <div className="card-body">
            {Array.from(cardBodyRenderer(coordinator))}
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  id: number;
}

@observer
export class X7PushableBlockComponent extends React.Component<Props> {
  readonly store: X7PushableBlockStore;

  constructor(props: Readonly<Props>) {
    super(props);
    this.store = new X7PushableBlockStore(props.id);
  }

  componentDidMount() {
    this.store.loader.load();
  }

  render() {
    return Array.from(componentRenderer(this.store));
  }

  componentWillUnmount(): void {
    this.store.dispose();
  }

}
